import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useServiceSelection } from '../contexts/ServiceSelectionContext';
import { servicesData, serviceCategories, getServicesByCategory } from '../data/servicesData';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

const ServiceCard = ({ service, isHighlighted }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { addService, isServiceSelected, selectedServices, removeService } = useServiceSelection();
  const navigate = useNavigate();

  const handleToggleService = (serviceItem, parentService) => {
    const itemId = `${parentService.id}-${serviceItem.name}`;
    if (isServiceSelected(itemId)) {
      removeService(itemId);
    } else {
      const serviceToAdd = {
        id: itemId,
        name: `${parentService.title} - ${serviceItem.name}`,
        price: serviceItem.price,
        duration: extractDuration(serviceItem.name, serviceItem.description),
        category: parentService.category,
        description: serviceItem.description
      };
      addService(serviceToAdd);
    }
  };

  const handleBookNow = () => {
    navigate('/booking');
  };

  // Extract duration from service name or description
  const extractDuration = (name, description) => {
    const durationMatch = name.match(/(\d+)\s*minute/) || 
                         name.match(/(\d+)\s*min/) ||
                         (description && description.match(/(\d+)\s*minute/));
    return durationMatch ? `${durationMatch[1]} min` : '30 min';
  };

  // Check how many of this service's items are selected
  const getSelectedCount = () => {
    return selectedServices.filter(s => s.id.startsWith(service.id)).length;
  };

  const selectedCount = getSelectedCount();

  return (
    <Card 
      id={service.id}
      className={`
        relative overflow-hidden transition-all duration-700 hover:shadow-xl
        ${isHighlighted ? 'ring-4 ring-blue-400 ring-offset-4 shadow-2xl scale-[1.02] bg-gradient-to-br from-blue-50 to-cyan-50' : 'hover:-translate-y-1'}
        ${selectedCount > 0 ? 'ring-2 ring-green-400' : ''}
      `}
    >
      {/* Selected indicator badge */}
      {selectedCount > 0 && (
        <div className="absolute top-4 right-4 z-10">
          <Badge className="bg-green-500 text-white border-0 px-3 py-1 flex items-center gap-1">
            {selectedCount} selected
          </Badge>
        </div>
      )}
      
      {/* Highlight indicator */}
      {isHighlighted && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse"></div>
      )}
      
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-cyan-700 bg-clip-text text-transparent">
              {service.title}
            </CardTitle>
            {service.description && (
              <p className="text-sm text-gray-600 mt-2">{service.description}</p>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid gap-3">
          {service.services.map((item, index) => {
            const itemId = `${service.id}-${item.name}`;
            const isSelected = isServiceSelected(itemId);
            
            return (
              <div 
                key={index}
                className={`
                  group relative p-4 rounded-xl border transition-all duration-300
                  ${isSelected 
                    ? 'border-green-400 bg-green-50' 
                    : isHighlighted 
                      ? 'border-blue-300 bg-white/80' 
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                  }
                `}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 space-y-1">
                    <h4 className="font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">
                      {item.name}
                    </h4>
                    {item.description && (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {item.price.toLowerCase().startsWith('starts at') ? (
                      <div className="text-right">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Starts at</div>
                        <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent whitespace-nowrap">
                          {item.price.replace(/^starts at\s*/i, '')}
                        </div>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent whitespace-nowrap">
                        {item.price}
                      </span>
                    )}
                    {item.price.toLowerCase().includes('contact') ? (
                      <Button
                        size="sm"
                        onClick={() => navigate('/contact')}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                      >
                        Contact Us
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleToggleService(item, service)}
                        className={`
                          transition-all duration-300
                          ${isSelected 
                            ? 'bg-red-500 hover:bg-red-600 text-white' 
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                          }
                        `}
                      >
                        {isSelected ? (
                          <>Remove from Booking</>
                        ) : (
                          <>Add to Booking</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <Button 
          onClick={handleBookNow}
          className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-md hover:shadow-lg transition-all duration-300 group"
        >
          Book {selectedCount > 0 ? 'Selected Services' : 'This Service'}
        </Button>
      </CardContent>
      
      {/* Decorative elements */}
      <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-2xl"></div>
    </Card>
  );
};

const CategorySection = ({ category, services, highlightedService }) => {
  // Center single service cards
  const gridClass = services.length === 1 
    ? "flex justify-center" 
    : "grid gap-6 md:grid-cols-2 lg:grid-cols-2";
    
  return (
    <div className="space-y-6">
      <div className={gridClass}>
        {services.map((service) => (
          <div key={service.id} className={services.length === 1 ? "max-w-2xl" : ""}>
            <ServiceCard 
              service={service}
              isHighlighted={highlightedService === service.id}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const Services = () => {
  const [activeCategory, setActiveCategory] = useState(serviceCategories[0]);
  const [highlightedService, setHighlightedService] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const categoryRefs = useRef({});
  const [isScrolling, setIsScrolling] = useState(false);
  const { getServiceCount } = useServiceSelection();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Handle navigation from home page with smooth highlighting
    if (location.state?.selectedService) {
      const { selectedService, selectedCategory } = location.state;
      setActiveCategory(selectedCategory);
      setHighlightedService(selectedService);
      
      // Scroll to the service after component renders
      setTimeout(() => {
        const element = document.getElementById(selectedService);
        if (element) {
          const yOffset = -150; // Offset for fixed headers
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
          
          // Add a pulsing effect
          element.classList.add('animate-pulse');
          setTimeout(() => {
            element.classList.remove('animate-pulse');
          }, 2000);
        }
      }, 500);

      // Remove highlight after 5 seconds
      setTimeout(() => {
        setHighlightedService(null);
      }, 5000);
    }
  }, [location]);

  const scrollToCategory = (category) => {
    setActiveCategory(category);
    setIsScrolling(true);
    
    const element = categoryRefs.current[category];
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
      
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  useEffect(() => {
    if (isScrolling) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const category of serviceCategories) {
        const element = categoryRefs.current[category];
        if (element && element.offsetTop <= scrollPosition && 
            element.offsetTop + element.offsetHeight > scrollPosition) {
          setActiveCategory(category);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolling]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Hero Section with Ocean Theme */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
        {/* Animated background patterns */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)',
          }}></div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-24">
          <div className="flex flex-col items-center justify-center text-center space-y-6">
            <div className="inline-flex items-center gap-2 text-white/90 font-medium">
              <span className="text-sm uppercase tracking-wider">Our Services</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white">
              Medical Spa <span className="text-cyan-300">Services</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Browse our selection of aesthetic treatments and find the right options for your goals
            </p>
          </div>
        </div>
        

      </div>

      {/* Sticky Category Navigation */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-md shadow-md border-b border-blue-100">
        <div className="container mx-auto px-4">
          <Tabs value={activeCategory} onValueChange={scrollToCategory} className="w-full">
            <TabsList className="w-full h-auto p-1 bg-blue-50/50 grid grid-cols-2 lg:grid-cols-4 gap-2">
              {serviceCategories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className={`
                    py-3 px-4 rounded-lg font-medium transition-all duration-300
                    ${activeCategory === category
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                      : 'hover:bg-white/70 hover:text-blue-700'
                    }
                  `}
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Services Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="space-y-24">
          {serviceCategories.map((category) => (
            <div 
              key={category}
              ref={el => categoryRefs.current[category] = el}
              className="scroll-mt-32"
            >
              <div className="text-center mb-12 space-y-4">
                <h2 className="text-4xl sm:text-5xl font-bold">
                  <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent bg-size-200 animate-gradient">
                    {category}
                  </span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto rounded-full"></div>
              </div>
              
              <CategorySection 
                category={category}
                services={getServicesByCategory(category)}
                highlightedService={highlightedService}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-radial-gradient(circle at 50% 50%, transparent 0, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
        }}></div>
        
        <div className="relative container mx-auto px-4 text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            Ready to Book?
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Schedule your appointment today at Oceanique
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/booking')}
              className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 px-8 py-6 text-lg font-semibold group"
            >
              Book Now
              {getServiceCount() > 0 && (
                <Badge className="ml-2 bg-blue-600 text-white">
                  {getServiceCount()}
                </Badge>
              )}
            </Button>
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 px-8 py-6 text-lg font-semibold"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
